import React, { useContext } from 'react';

import { Camera } from '@yandex-360/iconostasis/dist/icons/Camera';
import { CameraBlocked } from '@yandex-360/iconostasis/dist/icons/CameraBlocked';
import { CameraOff } from '@yandex-360/iconostasis/dist/icons/CameraOff';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { RootState } from '../../../../../../../init-store';
import { Button, SvgImage } from '../../../../../../shared/components';
import { selectShouldShowDeviceErrorModal } from '../../../../../../shared/features/device-manager/selectors';
import { isMobileUa } from '../../../../../../shared/features/uatraits';
import { i18n } from '../../../../../../shared/helpers/i18n';
import { MediaStatus } from '../../../../constants';
import { ToolbarContext } from '../../index';
import { ToolbarOrbTooltip } from '../toolbar-orb-tooltip';
import css from './style.css';
import { CameraButtonProps } from './types';

const shouldShowSelector = (state: RootState) => selectShouldShowDeviceErrorModal(state, 'videoinput');

export const CameraButton: React.FC<CameraButtonProps> = ({
    onToggleMute,
    status = MediaStatus.UNMUTED,
    buttonSize: buttonSizeProp,
    title: titleProp,
    testId: testIdProp,
    disabled,
    className,
    classNameWrapper,
    buttonProps: extraButtonProps,
}) => {
    const shouldShowError = useSelector(shouldShowSelector);
    const isMobile = useSelector(isMobileUa);
    const { buttonSize: contextButtonSize } = useContext(ToolbarContext);
    const buttonSize = buttonSizeProp ?? contextButtonSize;

    const isBadgeErrorVisible = shouldShowError && status !== MediaStatus.BLOCKED;

    const specific = (() => {
        switch (status) {
            case MediaStatus.BLOCKED:
                return {
                    icon: <CameraBlocked variant="solid" size="md" />,
                    title: i18n('toolbar', 'organizer-revoked-camera-permission'),
                    testId: 'organizer-revoked-camera-permission-button',
                    onClick: undefined,
                };
            case MediaStatus.UNMUTED:
                return {
                    icon: <Camera variant="solid" size="md" />,
                    title: i18n('toolbar', 'turn-off-camera'),
                    testId: 'turn-off-camera-button',
                    onClick: onToggleMute,
                };
            case MediaStatus.MUTED:
                return {
                    icon: <CameraOff variant="solid" size="md" />,
                    title: i18n('toolbar', 'turn-on-camera'),
                    testId: 'turn-on-camera-button',
                    onClick: onToggleMute,
                };
        }
    })();

    const title = titleProp ?? specific.title;
    const testId = testIdProp ?? specific.testId;

    const button = (
        <ToolbarOrbTooltip
            isDisabled={status !== MediaStatus.BLOCKED || isMobile}
            content={i18n('toolbar', 'organizer-revoked-camera-permission')}
        >
            <div>
                <Button
                    view="neutral"
                    size={buttonSize}
                    icon={specific.icon}
                    onClick={specific.onClick}
                    className={className ?? undefined}
                    isDisabled={disabled}
                    testId={testId}
                    title={title}
                    {...{ 'aria-label': title }}
                    {...extraButtonProps}
                />
            </div>
        </ToolbarOrbTooltip>
    );

    return (
        <div className={cn(css.CameraButton, classNameWrapper)}>
            {button}
            {isBadgeErrorVisible ? <SvgImage className={css.BadgeError} noContainer imageName="badgeError" /> : null}
        </div>
    );
};
